import { useCallback, useEffect, useState } from 'react';

import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import { useStores } from '@/components/hooks';
import { phoneNameSpace } from '@/components/Common/PhoneComp';
import { PhoneIdentity } from '@/types/types';

const passwordNameSpace = 'sharingBlock.recipientsList';

interface UseRecipientDetailsOptions {
    email: string;
    showPhones: boolean;
    showPasswords: boolean;
}

interface UseRecipientDetailsResult {
    currentPhone: { phone?: string; prefix?: string } | null;
    passwordInfo: { maskedPassword?: string | null; hasPassword?: boolean } | null;
    isPasswordSaving: boolean;
    phoneSaved: boolean;
    passwordSaved: boolean;
    onPhoneSubmit: (phoneNumber: PhoneIdentity) => Promise<void>;
    onPasswordSave: (password: string) => Promise<void>;
}

const useRecipientDetails = ({
    email,
    showPhones,
    showPasswords,
}: UseRecipientDetailsOptions): UseRecipientDetailsResult => {
    const { t } = useTranslation();
    const {
        sharedUsersStore: { setRecipientPassword, getRecipientPasswordInfo },
        usersPhonesStore: { changePhoneNumber, getUserPhone, fetchUserPhone },
    } = useStores();

    const [isPasswordSaving, setIsPasswordSaving] = useState(false);
    const [phoneSaved, setPhoneSaved] = useState(false);
    const [passwordSaved, setPasswordSaved] = useState(false);

    const { phonesVector: { currentPhone } } = getUserPhone(email);
    const passwordInfo = showPasswords ? getRecipientPasswordInfo(email) : null;

    useEffect(() => {
        if (showPhones) {
            fetchUserPhone(email);
        }
    }, [email, showPhones]);

    const onPhoneSubmit = useCallback(async (phoneNumber: PhoneIdentity) => {
        const result = await changePhoneNumber({
            email,
            phone: phoneNumber.phone,
            prefix: phoneNumber.prefix,
        });
        if (result.success) {
            message.success(t(`${phoneNameSpace}.successfullyNumberChange`));
            setPhoneSaved(true);
        } else {
            const errorMsg = result.errorMessage || t(`${phoneNameSpace}.invalidPhone`);
            message.error(errorMsg);
        }
    }, [email, changePhoneNumber, t]);

    const onPasswordSave = useCallback(async (password: string): Promise<void> => {
        setIsPasswordSaving(true);
        const result = await setRecipientPassword(email, password);
        if (result.success) {
            message.success(t(`${passwordNameSpace}.passwordUpdated`));
            setPasswordSaved(true);
        } else {
            const errorMsg = result.errorMessage || t(`${passwordNameSpace}.passwordUpdateError`);
            message.error(errorMsg);
        }
        setIsPasswordSaving(false);
    }, [email, setRecipientPassword, t]);

    return {
        currentPhone,
        passwordInfo,
        isPasswordSaving,
        phoneSaved,
        passwordSaved,
        onPhoneSubmit,
        onPasswordSave,
    };
};

export default useRecipientDetails;
